// PrintedOrdersPage.js
import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchPrintedOrders, updateOrderStatus, deleteOrder, fetchPrintedOrdersSummary, checkDuplicatePhoneCount, markOrdersSentToFincartBulk } from "../../api/OrderAPIs";
import {
  fetchFincartGovernorates,
  getDefaultPickupLocationId,
  sendOrderToFincart,
  saveFincartOrderId,
  getStoredFincartOrderId,
} from "../../api/FincartAPIs";
import { fetchDesignById } from "../../api/DesignAPIs";
import { fetchTypeById } from "../../api/TypeAPIs";
import { useAuth } from "../../utils/auth";
import { canViewOrders, canMutateOrders, isAdmin } from "../../utils/roles";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL_V2 } from "../../api/URL_Header";
import EditOrderModal from "./EditOrderModal";
import {
  ChevronRight,
  ChevronLeft,
  Package,
  User,
  Phone,
  MapPin,
  Map,
  Copy,
  Download,
  Link as LinkIcon,
  Edit,
  Trash2,
  Archive,
  Layers,
  Filter,
  Search,
  Loader,
  Printer,
  Building2,
  CheckSquare,
  CheckCircle,
  Send,
} from "lucide-react";

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  } else if (type === "card") {
    return (
      <div className="animate-pulse bg-white/80 backdrop-blur-sm rounded-2xl p-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  } else if (type === "productsCard") {
    return (
      <div className="animate-pulse bg-white/80 backdrop-blur-sm rounded-2xl p-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="h-6 bg-gray-200 rounded w-3/4"></div>
      </div>
    );
  }
  return null;
};

const PrintedOrdersPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canAct = canMutateOrders(authState.role);
  const [loadedOrders, setLoadedOrders] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [designFilter, setDesignFilter] = useState("all"); // "all" | "custom" | "normal"
  /** all | sent | notSent | dup */
  const [fincartFilter, setFincartFilter] = useState("all");
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [selectedOrderNumbers, setSelectedOrderNumbers] = useState(new Set());
  const [isMarkingGirabatcom, setIsMarkingGirabatcom] = useState(false);
  const [isMarkingAsOld, setIsMarkingAsOld] = useState(false);
  const [isSendingFincart, setIsSendingFincart] = useState(false);

  const { data: printedOrdersSummary, isLoading: isLoadingSummary } = useQuery({
    queryKey: ["printedOrdersSummary"],
    queryFn: fetchPrintedOrdersSummary,
    enabled: canViewOrders(authState.role),
    staleTime: Infinity,
    placeholderData: { count: 0, totalProfit: 0, totalAmount: 0 },
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    if (!canViewOrders(authState.role)) {
      navigate("/login");
      return;
    }
    loadInitialOrders();
  }, [authState, navigate]);

  const isLoading = isLoadingSummary;

  const handleDownload = async (url, filename = "design.jpg") => {
    try {
      const response = await fetch(
        `${API_BASE_URL_V2}/api/files/download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename)}`
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("فشل التحميل. تحقق من السيرفر.");
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).then(() => {
        // toast.success("تم النسخ بنجاح!");
      }).catch(() => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    textArea.style.left = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        // toast.success("تم النسخ بنجاح!");
      } else {
        toast.error("فشل النسخ، حاول مرة أخرى.");
      }
    } catch (err) {
      toast.error("فشل النسخ، حاول النسخ يدويًا.");
      console.error("Copy failed:", err);
    }

    document.body.removeChild(textArea);
  };

  const enrichOrders = async (data) => {
    return await Promise.all(
      data.map(async (order) => {
        let enrichedItems = [];

        for (const detail of order.orderDetails) {
          const design = await fetchDesignById(detail.designId);
          const type = await fetchTypeById(detail.typeId);
          if (!design || !type) continue;

          const baseItem = {
            id: detail.designId,
            name: design.Name,
            price: detail.price,
            type: type.name,
            driveLink: design.code.startsWith("CUSTOM") 
              ? `${API_BASE_URL_V2}${design.driveLink}` 
              : design.driveLink || "",
            code: design.code || "None",
          };

          for (let i = 0; i < detail.quantity; i++) {
            enrichedItems.push({
              ...baseItem,
              quantityIndex: i + 1,
              totalQuantity: detail.quantity,
            });
          }
        }

        return {
          orderNumber: order.orderId,
          items: enrichedItems,
          total: order.totalAmount,
          profit: order.profit || 0,
          date: new Date(order.date).toISOString().split("T")[0],
          address: order.address?.fullAddress || "غير معروف",
          state: order.address?.state || "غير معروف",
          city: order.address?.city || "",
          apartment: order.address?.apartment || "",
          phoneNumber: order.phoneNumber || "غير معروف",
          phoneNumber2: order.phoneNumber2 || "غير معروف",
          name: order.name || "غير معروف",
          note: order.note || "",
          status: order.status || "غير معروف",
          sentToFincart: !!order.sentToFincart || !!getStoredFincartOrderId(order.orderId),
          fincartOrderId: getStoredFincartOrderId(order.orderId),
        };
      })
    );
  };

  const loadInitialOrders = async () => {
    setIsOrdersLoading(true);
    try {
      const data = await fetchPrintedOrders(0, pageSize);
      if (data.length === 0 || data.length < pageSize) {
        setHasMore(false);
      }
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders(enrichedOrders);
      setCurrentPage(1);
      backfillSentToFincartFromLocalStorage(enrichedOrders);
    } catch (err) {
      console.error("Error loading initial orders:", err);
      setError("فشل في تحميل الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل الطلبات!");
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const loadMoreOrders = async () => {
    setIsLoadingMore(true);
    try {
      const data = await fetchPrintedOrders(currentPage, pageSize);
      if (data.length === 0 || data.length < pageSize) {
        setHasMore(false);
      }
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders((prev) => [...prev, ...enrichedOrders]);
      setCurrentPage((prev) => prev + 1);
      backfillSentToFincartFromLocalStorage(enrichedOrders);
    } catch (err) {
      console.error("Error loading more orders:", err);
      setError("فشل في تحميل المزيد من الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل المزيد من الطلبات!");
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handlePageSizeChange = async (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
    setIsOrdersLoading(true);
    try {
      const data = await fetchPrintedOrders(0, newSize);
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders(enrichedOrders);
      setCurrentPage(1);
      setHasMore(data.length === newSize);
      backfillSentToFincartFromLocalStorage(enrichedOrders);
    } catch (err) {
      console.error("Error changing page size:", err);
      toast.error("فشل في تحميل الطلبات!");
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    if (!canAct) return;
    const reason = "no reason (normal order)";

    if (newStatus === "Old") {
      try {
        const confirmUpdate = window.confirm("هل تريد تحديث الطلب إلى قديم؟");
        if (!confirmUpdate) return;

        await updateOrderStatus(orderId, newStatus, reason);
        setLoadedOrders(prev => prev.filter(o => o.orderNumber !== orderId));
        setSelectedOrderNumbers((prev) => {
          const next = new Set(prev);
          next.delete(orderId);
          return next;
        });
        toast.success("تم تحديث الطلب إلى قديم!");
      } catch (err) {
        console.error(err);
        toast.error("فشل في العملية: " + (err.message || "خطأ غير معروف"));
      }
      return;
    }

    const confirm = window.confirm(`تأكيد تحديث الحالة إلى "${newStatus}"؟`);
    if (!confirm) return;

    try {
      await updateOrderStatus(orderId, newStatus, reason);
      setLoadedOrders(prev => prev.filter(o => o.orderNumber !== orderId));
      setSelectedOrderNumbers((prev) => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
      toast.success(`تم تحديث الطلب إلى ${newStatus} بنجاح`);
    } catch (err) {
      toast.error("فشل التحديث: " + err.message);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!canAct) return;
    if (!canMutateOrders(authState.role)) return;
    const confirmDelete = window.confirm(`هل أنت متأكد من حذف الطلب رقم ${orderId}؟`);
    if (!confirmDelete) return;
    try {
      await deleteOrder(orderId);
      setLoadedOrders((prev) =>
        prev.filter((order) => order.orderNumber !== orderId)
      );
      setSelectedOrderNumbers((prev) => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
      toast.success(`تم حذف الطلب رقم ${orderId} بنجاح!`);
    } catch (err) {
      console.error("Error deleting order:", err);
      toast.error("فشل في حذف الطلب. يرجى المحاولة مرة أخرى.");
    }
  };

  const toggleSelectOrder = (orderNumber) => {
    setSelectedOrderNumbers((prev) => {
      const next = new Set(prev);
      if (next.has(orderNumber)) next.delete(orderNumber);
      else next.add(orderNumber);
      return next;
    });
  };

  const setSelectAllVisible = (checked, visibleOrders) => {
    setSelectedOrderNumbers((prev) => {
      const next = new Set(prev);
      if (checked) {
        visibleOrders.forEach((o) => next.add(o.orderNumber));
      } else {
        visibleOrders.forEach((o) => next.delete(o.orderNumber));
      }
      return next;
    });
  };

  const handleCopySelectedPhones = () => {
    const selected = loadedOrders.filter((o) =>
      selectedOrderNumbers.has(o.orderNumber)
    );

    if (selected.length === 0) {
      toast.info("اختر أوردرات أولاً");
      return;
    }

    const phones = selected
      .map((o) => (o.phoneNumber || "").trim())
      .filter((phone) => phone && phone !== "غير معروف");

    if (phones.length === 0) {
      toast.info("لا يوجد أرقام موبايل للنسخ");
      return;
    }

    const text = phones.join("\n");
    copyToClipboard(text);
    toast.success(`تم نسخ ${phones.length} رقم موبايل`);
  };

  const handleMarkSelectedAsGirabatcom = async () => {
    if (!canAct) return;

    const selected = loadedOrders.filter((o) =>
      selectedOrderNumbers.has(o.orderNumber)
    );

    if (selected.length === 0) {
      toast.info("اختر أوردرات أولاً");
      return;
    }

    const confirmed = window.confirm(
      `هل تريد تحويل ${selected.length} طلب(ات) محددة إلى "Girabatcom"؟\n\nستختفي من الصفحة فورًا.`
    );
    if (!confirmed) return;

    setIsMarkingGirabatcom(true);
    let successCount = 0;
    let failedCount = 0;
    const succeededIds = [];

    toast.info("جاري تحويل المحدد إلى Girabatcom... لا تغلق الصفحة", {
      autoClose: false,
      toastId: "marking-girabatcom",
    });

    for (const order of selected) {
      try {
        await updateOrderStatus(
          order.orderNumber,
          "Girabatcom",
          "تم التحويل لـ Girabatcom من Printed"
        );
        succeededIds.push(order.orderNumber);
        successCount++;
      } catch (err) {
        console.error(`فشل تحديث الطلب ${order.orderNumber}:`, err);
        failedCount++;
      }
    }

    if (succeededIds.length > 0) {
      setLoadedOrders((prev) =>
        prev.filter((order) => !succeededIds.includes(order.orderNumber))
      );
      setSelectedOrderNumbers((prev) => {
        const next = new Set(prev);
        succeededIds.forEach((id) => next.delete(id));
        return next;
      });
    }

    toast.dismiss("marking-girabatcom");
    setIsMarkingGirabatcom(false);

    if (successCount === 0) {
      toast.error(
        `فشل تحويل الطلبات إلى Girabatcom` +
          (failedCount > 0 ? ` (${failedCount} طلب)` : "")
      );
      return;
    }

    toast.success(
      `تم تحويل ${successCount} طلب(ات) إلى Girabatcom بنجاح!` +
        (failedCount > 0 ? `\nفشل في ${failedCount} طلب(ات)` : ""),
      { autoClose: 6000 }
    );
  };

  const handleMarkSelectedAsOld = async () => {
    if (!canAct) return;

    const selected = loadedOrders.filter((o) =>
      selectedOrderNumbers.has(o.orderNumber)
    );

    if (selected.length === 0) {
      toast.info("اختر أوردرات أولاً");
      return;
    }

    const confirmed = window.confirm(
      `هل تريد تحويل ${selected.length} طلب(ات) محددة إلى "قديم"؟\n\nستختفي من الصفحة فورًا.`
    );
    if (!confirmed) return;

    setIsMarkingAsOld(true);
    let successCount = 0;
    let failedCount = 0;
    const succeededIds = [];

    toast.info("جاري تحويل المحدد إلى قديم... لا تغلق الصفحة", {
      autoClose: false,
      toastId: "marking-old",
    });

    for (const order of selected) {
      try {
        await updateOrderStatus(
          order.orderNumber,
          "Old",
          "تم التحويل للقديم من Printed"
        );
        succeededIds.push(order.orderNumber);
        successCount++;
      } catch (err) {
        console.error(`فشل تحديث الطلب ${order.orderNumber}:`, err);
        failedCount++;
      }
    }

    if (succeededIds.length > 0) {
      setLoadedOrders((prev) =>
        prev.filter((order) => !succeededIds.includes(order.orderNumber))
      );
      setSelectedOrderNumbers((prev) => {
        const next = new Set(prev);
        succeededIds.forEach((id) => next.delete(id));
        return next;
      });
    }

    toast.dismiss("marking-old");
    setIsMarkingAsOld(false);

    if (successCount === 0) {
      toast.error(
        `فشل تحويل الطلبات إلى قديم` +
          (failedCount > 0 ? ` (${failedCount} طلب)` : "")
      );
      return;
    }

    toast.success(
      `تم تحويل ${successCount} طلب(ات) إلى قديم بنجاح!` +
        (failedCount > 0 ? `\nفشل في ${failedCount} طلب(ات)` : ""),
      { autoClose: 6000 }
    );
  };

  const applyFincartIdUpdates = (idUpdates) => {
    if (Object.keys(idUpdates).length === 0) return;
    setLoadedOrders((prev) =>
      prev.map((o) => {
        const update = idUpdates[o.orderNumber];
        if (!update) return o;
        const fincartOrderId =
          typeof update === "object" ? update.fincartOrderId : update;
        const alreadyInFincart =
          typeof update === "object" ? !!update.alreadyInFincart : false;
        return {
          ...o,
          fincartOrderId: fincartOrderId || o.fincartOrderId,
          sentToFincart: true,
          fincartSentThisSession: !alreadyInFincart,
          fincartDupSkipped: false,
        };
      })
    );
  };

  const markOrdersAsOldInFincart = (orders) => {
    if (!orders?.length) return;
    const nums = new Set(orders.map((o) => o.orderNumber));
    setLoadedOrders((prev) =>
      prev.map((o) =>
        nums.has(o.orderNumber)
          ? {
              ...o,
              sentToFincart: true,
              fincartSentThisSession: false,
              fincartDupSkipped: false,
            }
          : o
      )
    );
  };

  const markFincartDupSkipped = (orders) => {
    if (!orders?.length) return;
    const nums = new Set(orders.map((o) => o.orderNumber));
    setLoadedOrders((prev) =>
      prev.map((o) =>
        nums.has(o.orderNumber) ? { ...o, fincartDupSkipped: true } : o
      )
    );
  };

  const persistSentToFincart = async (orderIds) => {
    if (!orderIds?.length) return;
    try {
      await markOrdersSentToFincartBulk(orderIds, true);
    } catch (err) {
      console.error("فشل حفظ SentToFincart في الداتابيز:", err);
      toast.warning(
        "تم الإرسال لـ Fincart لكن فشل حفظ العلامة في السيرفر — حدّث يدوياً لو لزم"
      );
    }
  };

  const backfillSentToFincartFromLocalStorage = async (orders) => {
    const fromStorage = orders
      .filter((o) => getStoredFincartOrderId(o.orderNumber))
      .map((o) => o.orderNumber);
    if (fromStorage.length === 0) return;
    try {
      await markOrdersSentToFincartBulk(fromStorage, true);
    } catch (err) {
      console.error("فشل مزامنة SentToFincart من localStorage:", err);
    }
  };

  const sendOrdersBatchToFincart = async (orders) => {
    let successCount = 0;
    let alreadyInFincartCount = 0;
    let failedCount = 0;
    const failedDetails = [];
    const alreadyInFincartDetails = [];
    const idUpdates = {};

    setIsSendingFincart(true);
    toast.info("جاري الإرسال إلى Fincart... لا تغلق الصفحة", {
      autoClose: false,
      toastId: "fincart-send",
    });

    try {
      const [pickupLocationId, governorates] = await Promise.all([
        getDefaultPickupLocationId(),
        fetchFincartGovernorates(),
      ]);

      for (const order of orders) {
        try {
          const result = await sendOrderToFincart(
            order,
            pickupLocationId,
            governorates
          );
          const fincartOrderId = result?.data?.orderId;
          const alreadyInFincart = !!result?.alreadyInFincart;

          if (fincartOrderId) {
            saveFincartOrderId(order.orderNumber, fincartOrderId);
            idUpdates[order.orderNumber] = { fincartOrderId, alreadyInFincart };
          } else {
            idUpdates[order.orderNumber] = {
              fincartOrderId: null,
              alreadyInFincart,
            };
          }

          if (alreadyInFincart) {
            alreadyInFincartCount++;
            alreadyInFincartDetails.push(`#${order.orderNumber}`);
          } else {
            successCount++;
          }
        } catch (err) {
          failedCount++;
          failedDetails.push(`#${order.orderNumber}: ${err.message}`);
          console.error(`Fincart failed #${order.orderNumber}:`, err);
        }
      }
    } catch (err) {
      toast.dismiss("fincart-send");
      setIsSendingFincart(false);
      throw err;
    }

    const sentIds = Object.keys(idUpdates).map((id) => Number(id) || id);
    await persistSentToFincart(sentIds);
    applyFincartIdUpdates(idUpdates);
    toast.dismiss("fincart-send");
    setIsSendingFincart(false);

    return {
      successCount,
      alreadyInFincartCount,
      alreadyInFincartDetails,
      failedCount,
      failedDetails,
    };
  };

  const reportFincartSendResult = (
    successCount,
    failedCount,
    failedDetails,
    alreadyInFincartCount = 0,
    alreadyInFincartDetails = []
  ) => {
    if (successCount > 0) {
      toast.success(
        `تم إرسال ${successCount} طلب(ات) إلى Fincart بنجاح` +
          (failedCount > 0 ? `\nفشل ${failedCount}` : ""),
        { autoClose: 7000 }
      );
    }

    if (alreadyInFincartCount > 0) {
      toast.warning(
        `${alreadyInFincartCount} طلب كان موجود في Fincart (عميل/أوردر مكرر):\n${alreadyInFincartDetails.slice(0, 8).join(", ")}` +
          (alreadyInFincartDetails.length > 8 ? " ..." : "") +
          `\nاتعلموا كـ old in fincart`,
        { autoClose: 10000 }
      );
    }

    if (failedCount > 0) {
      toast.error(
        `فشل إرسال ${failedCount} طلب(ات):\n${failedDetails.slice(0, 5).join("\n")}` +
          (failedDetails.length > 5 ? "\n..." : ""),
        { autoClose: 12000 }
      );
    }
  };

  const handleSendSelectedToFincart = async () => {
    if (!canAct) return;

    const selected = loadedOrders.filter((o) =>
      selectedOrderNumbers.has(o.orderNumber)
    );

    if (selected.length === 0) {
      toast.info("اختر أوردرات أولاً");
      return;
    }

    const alreadySent = selected.filter((o) => o.sentToFincart);
    const toSend = selected.filter((o) => !o.sentToFincart);

    if (alreadySent.length > 0) {
      markOrdersAsOldInFincart(alreadySent);
    }

    if (toSend.length === 0) {
      toast.info("كل الأوردرات المحددة مُرسلة لـ Fincart بالفعل");
      return;
    }

    const missingArea = toSend.filter((o) => !o.city?.trim());
    if (missingArea.length > 0) {
      toast.error(
        `${missingArea.length} طلب بدون مدينة/منطقة من قائمة Fincart (مثال: #${missingArea[0].orderNumber})`
      );
      return;
    }

    toast.info("جاري فحص التكرار قبل الإرسال إلى Fincart...", {
      autoClose: false,
      toastId: "fincart-dup-check",
    });

    const cleanOrders = [];
    const duplicateOrders = [];

    for (const order of toSend) {
      try {
        const count = await checkDuplicatePhoneCount(
          order.orderNumber,
          order.phoneNumber,
          order.phoneNumber2 || ""
        );
        if (count > 0) {
          duplicateOrders.push({ order, count });
        } else {
          cleanOrders.push(order);
        }
      } catch (err) {
        console.error(`فشل فحص التكرار #${order.orderNumber}:`, err);
        toast.warning(
          `فشل فحص التكرار لـ #${order.orderNumber} — اعتبره متكرر للمراجعة`
        );
        duplicateOrders.push({ order, count: "?" });
      }
    }

    toast.dismiss("fincart-dup-check");

    if (toSend.length === 1) {
      const only = toSend[0];
      const dup = duplicateOrders[0];

      if (dup) {
        const agreeDuplicate = window.confirm(
          `تحذير: هذا العميل لديه ${dup.count} طلب(ات) سابقة بنفس الرقم!\n\nالطلب #${only.orderNumber} — ${only.name}\nالرقم: ${only.phoneNumber}\n\nهل توافق على المتابعة؟`
        );
        if (!agreeDuplicate) {
          markFincartDupSkipped([only]);
          toast.info("تم إلغاء الإرسال بسبب تكرار الرقم");
          return;
        }
      }

      const confirmSend = window.confirm(
        `هل توافق على إرسال الطلب #${only.orderNumber} إلى Fincart للشحن؟` +
          (alreadySent.length > 0
            ? `\n\n(سيتم تخطي ${alreadySent.length} مُرسلين مسبقاً من التحديد)`
            : "") +
          `\n\nلن يتم تغيير حالة الطلب — حدّث يدوياً بعدين.`
      );
      if (!confirmSend) return;

      try {
        const {
          successCount,
          failedCount,
          failedDetails,
          alreadyInFincartCount,
          alreadyInFincartDetails,
        } = await sendOrdersBatchToFincart([only]);
        reportFincartSendResult(
          successCount,
          failedCount,
          failedDetails,
          alreadyInFincartCount,
          alreadyInFincartDetails
        );
      } catch (err) {
        toast.error(err.message || "فشل التحضير للإرسال إلى Fincart");
      }
      return;
    }

    let totalSuccess = 0;
    let totalFailed = 0;
    let totalAlreadyInFincart = 0;
    const allFailedDetails = [];
    const allAlreadyInFincartDetails = [];

    if (cleanOrders.length > 0) {
      const confirmClean = window.confirm(
        `إرسال ${cleanOrders.length} طلب(ات) إلى Fincart للشحن؟` +
          (duplicateOrders.length > 0
            ? `\n\nسيتم تخطي ${duplicateOrders.length} طلب(ات) متكررة مؤقتاً — هسألك عنهم بعد الإرسال`
            : "") +
          (alreadySent.length > 0
            ? `\n\n(سيتم تخطي ${alreadySent.length} مُرسلين مسبقاً)`
            : "") +
          `\n\nلن يتم تغيير حالة الطلب — حدّث يدوياً بعدين.`
      );
      if (!confirmClean) {
        toast.info("تم إلغاء الإرسال");
        return;
      }

      try {
        const {
          successCount,
          failedCount,
          failedDetails,
          alreadyInFincartCount,
          alreadyInFincartDetails,
        } = await sendOrdersBatchToFincart(cleanOrders);
        totalSuccess += successCount;
        totalFailed += failedCount;
        totalAlreadyInFincart += alreadyInFincartCount;
        allFailedDetails.push(...failedDetails);
        allAlreadyInFincartDetails.push(...alreadyInFincartDetails);
      } catch (err) {
        toast.error(err.message || "فشل التحضير للإرسال إلى Fincart");
        return;
      }
    }

    if (duplicateOrders.length > 0) {
      const dupList = duplicateOrders
        .slice(0, 15)
        .map(
          ({ order, count }) =>
            `#${order.orderNumber} — ${order.name} (${count} سابق)`
        )
        .join("\n");

      const sendDuplicates = window.confirm(
        `فيه ${duplicateOrders.length} طلب(ات) متكررة مراحتش لـ Fincart.\n\n${dupList}` +
          (duplicateOrders.length > 15 ? "\n..." : "") +
          `\n\nهل عايز تبعتها برضو؟`
      );

      if (sendDuplicates) {
        try {
          const {
            successCount,
            failedCount,
            failedDetails,
            alreadyInFincartCount,
            alreadyInFincartDetails,
          } = await sendOrdersBatchToFincart(
            duplicateOrders.map(({ order }) => order)
          );
          totalSuccess += successCount;
          totalFailed += failedCount;
          totalAlreadyInFincart += alreadyInFincartCount;
          allFailedDetails.push(...failedDetails);
          allAlreadyInFincartDetails.push(...alreadyInFincartDetails);
          if (failedCount > 0) {
            const failedNums = new Set(
              failedDetails
                .map((d) => {
                  const m = d.match(/^#([^:]+):/);
                  return m ? String(m[1]).trim() : null;
                })
                .filter(Boolean)
            );
            markFincartDupSkipped(
              duplicateOrders
                .map(({ order }) => order)
                .filter((o) => failedNums.has(String(o.orderNumber)))
            );
          }
        } catch (err) {
          markFincartDupSkipped(duplicateOrders.map(({ order }) => order));
          toast.error(err.message || "فشل التحضير لإرسال المتكرر إلى Fincart");
        }
      } else {
        markFincartDupSkipped(duplicateOrders.map(({ order }) => order));
        toast.info(`تم تخطي ${duplicateOrders.length} طلب(ات) متكررة`);
      }
    }

    reportFincartSendResult(
      totalSuccess,
      totalFailed,
      allFailedDetails,
      totalAlreadyInFincart,
      allAlreadyInFincartDetails
    );
  };

  const handleOrderEdited = async (updatedOrder) => {
    if (!updatedOrder?.orderId) {
      await loadInitialOrders();
      return;
    }
    try {
      const [enriched] = await enrichOrders([updatedOrder]);
      setLoadedOrders((prev) =>
        prev.map((o) => (o.orderNumber === enriched.orderNumber ? enriched : o))
      );
    } catch (err) {
      console.error(err);
      await loadInitialOrders();
    }
  };

  const handleCopyOrderInfo = (order) => {
    const phone = `${order.phoneNumber}${order.phoneNumber2 !== "غير معروف" ? "-" + order.phoneNumber2 : ""}`;
    const formattedState = `${order.state} (D)`;
    const text = `${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD`;
    copyToClipboard(text);
  };

  const handleCopyItems = (order) => {
    const lines = order.items.map((item, index) => {
      const itemNum = `${index + 1}-${order.items.length}`;
      const type = item.type || "Unknown Type";
      const driveLink = item.driveLink || "No Link";
      return `${itemNum}\t\tPhone Cover (H)\t${type}\t\t1\t${driveLink}`;
    }).join('\n');
    copyToClipboard(lines);
  };

    const handleCopyAllExcelDataNoReference = async (order) => {
    try {
      // Format phone number
      const phone = `${order.phoneNumber}${
        order.phoneNumber2 && order.phoneNumber2 !== "غير معروف" ? "-" + order.phoneNumber2 : ""
      }`;
      
      const formattedState = `${order.state} (D)`;
      
      // No reference field - using empty string or tab
      const customerBase = `${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD\t`;

      const items = order.items;
      const lines = [];

      items.forEach((item, index) => {
        const itemNum = `${index + 1}-${items.length}`;
        const type = item.type || "Unknown Type";
        const driveLink = item.driveLink || "No Link";

        const itemData = `\t\tPhone Cover (H)\t${type}\t\t1\t${driveLink}`;
        const itemLine = `${itemNum}${itemData}`;

        if (index === 0) {
          lines.push(`${customerBase}\t\t${itemLine}`);
        } else {
          lines.push(`\t\t\t\t\t\t\t\t\t${itemLine}`);
        }
      });

      const finalText = lines.join('\n');
      copyToClipboard(finalText);

      // toast.success("تم نسخ الطلب كامل بنجاح (بدون مرجع)!");
    } catch (err) {
      console.error("خطأ في النسخ:", err);
      toast.error("فشل في النسخ، حاول مرة تانية");
    }
  };

  const handleCopyAddressClarification = () => {
    const message = `أهلاً بحضرتك

العنوان المسجل غير واضح أو ناقص تفاصيل مهمة لتسليم الطلب بنجاح

برجاء توضيح العنوان بالتفصيل:
• اسم الشارع
• رقم العمارة / الشقة
• معالم قريبة (مثل: بجوار صيدلية، أمام مدرسة، إلخ)

شكرًا لتفهمك، هنبدأ تجهيز الطلب فور توضيح العنوان`;

    copyToClipboard(message);
  };

  const isOrderCustom = (order) =>
    order.items.some((item) => item.code && item.code.startsWith("CUSTOM"));

  const isOrderNonCustom = (order) => !isOrderCustom(order);

  const filteredOrders = loadedOrders.filter((order) => {
    if (designFilter === "custom" && !isOrderCustom(order)) return false;
    if (designFilter === "normal" && !isOrderNonCustom(order)) return false;

    if (fincartFilter === "sent" && !order.sentToFincart) return false;
    if (fincartFilter === "notSent" && (order.sentToFincart || order.fincartDupSkipped))
      return false;
    if (fincartFilter === "dup" && !order.fincartDupSkipped) return false;

    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      order.orderNumber.toString().includes(term) ||
      order.name.toLowerCase().includes(term) ||
      order.phoneNumber.includes(term) ||
      order.phoneNumber2?.includes(term)
    );
  });

  const fincartFilterCounts = {
    all: loadedOrders.length,
    sent: loadedOrders.filter((o) => o.sentToFincart).length,
    notSent: loadedOrders.filter((o) => !o.sentToFincart && !o.fincartDupSkipped)
      .length,
    dup: loadedOrders.filter((o) => o.fincartDupSkipped).length,
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <ToastContainer position="top-center" />

      {editingOrderId && (
        <EditOrderModal
          orderId={editingOrderId}
          onClose={() => setEditingOrderId(null)}
          onSaved={handleOrderEdited}
        />
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <Printer className="text-slate-700" size={32} />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-slate-700 to-cyan-600 bg-clip-text text-transparent">
              Printed
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* <button
              onClick={() => navigate("/follow-up-phones")}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
            >
              <Phone size={20} />
              متابعة WhatsApp
            </button> */}
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 bg-white/80 backdrop-blur-sm text-gray-700 px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200"
            >
              <ChevronRight size={20} />
              العودة للرئيسية
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {canViewOrders(authState.role) && (
          <div className="grid grid-cols-1 md:grid-cols-1 gap-6 mb-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 border border-white/30 hover:shadow-2xl transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-amber-100 rounded-xl">
                  <Package className="text-amber-700" size={24} />
                </div>
                <h2 className="text-xl font-bold text-gray-800">ملخص Printed</h2>
              </div>
              {isLoading ? (
                <SkeletonLoader type="card" />
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-2 bg-slate-50 rounded-xl">
                    <span className="text-gray-600">عدد الطلبات</span>
                    <span className="text-2xl font-bold text-slate-700">{printedOrdersSummary.count} طلبات</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-green-50 rounded-xl">
                    <span className="text-gray-600">إجمالي المبلغ</span>
                    <span className="text-2xl font-bold text-green-600">{printedOrdersSummary.totalAmount} ج.م</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Search and Filter Bar */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="بحث برقم الطلب أو اسم العميل أو رقم الهاتف..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-slate-300 focus:border-slate-400 transition-all"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-6 py-3 border-2 rounded-xl transition-all ${
                showFilters || designFilter !== "all" || fincartFilter !== "all"
                  ? "bg-cyan-50 border-cyan-400 text-cyan-800"
                  : "bg-white border-gray-200 hover:bg-gray-50"
              }`}
            >
              <Filter size={20} />
              تصفية
              {(designFilter !== "all" || fincartFilter !== "all") && (
                <span className="text-xs font-bold bg-cyan-600 text-white rounded-full px-2 py-0.5">
                  {(designFilter !== "all" ? 1 : 0) + (fincartFilter !== "all" ? 1 : 0)}
                </span>
              )}
            </button>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
              <div className="flex flex-wrap items-center gap-2 justify-end">
                <span className="text-sm text-gray-600 ml-2">Fincart:</span>
                {[
                  { key: "all", label: "الكل", count: fincartFilterCounts.all, active: "bg-indigo-600 text-white border-indigo-600" },
                  { key: "notSent", label: "مراحش Fincart", count: fincartFilterCounts.notSent, active: "bg-gray-700 text-white border-gray-700" },
                  { key: "sent", label: "راح Fincart", count: fincartFilterCounts.sent, active: "bg-orange-500 text-white border-orange-500" },
                  { key: "dup", label: "DUP", count: fincartFilterCounts.dup, active: "bg-red-600 text-white border-red-600" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setFincartFilter(opt.key)}
                    className={`px-4 py-2 rounded-xl border-2 text-sm font-bold transition-all ${
                      fincartFilter === opt.key
                        ? opt.active
                        : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {opt.label} ({opt.count})
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2 justify-end">
                <span className="text-sm text-gray-600 ml-2">نوع التصميم:</span>
                {[
                  { value: "all", label: "كل الطلبات" },
                  { value: "custom", label: "طلبات مخصصة (CUSTOM)" },
                  { value: "normal", label: "طلبات عادية" },
                ].map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => setDesignFilter(value)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      designFilter === value
                        ? value === "custom"
                          ? "bg-green-600 text-white shadow-md"
                          : value === "normal"
                            ? "bg-gray-600 text-white shadow-md"
                            : "bg-slate-600 text-white shadow-md"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Page Size Selector */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <label className="font-medium text-gray-700">عرض:</label>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-slate-300 focus:border-slate-400 transition-all bg-white"
                disabled={isOrdersLoading}
              >
                <option value={10}>10 طلبات</option>
                <option value={20}>20 طلب</option>
                <option value={30}>30 طلب</option>
                <option value={40}>40 طلب</option>
                <option value={50}>50 طلب</option>
                <option value={80}>80 طلب</option>
                <option value={100}>100 طلب</option>
              </select>
              <span className="text-sm text-gray-600">(إجمالي المعروض: {loadedOrders.length})</span>
            </div>
            {isOrdersLoading && (
              <div className="flex items-center gap-2 text-slate-600">
                <Loader className="animate-spin" size={20} />
                <span>جاري التحميل...</span>
              </div>
            )}
          </div>
        </div>

        {/* Bulk actions */}
        <div className="bg-gradient-to-r from-cyan-50 to-slate-50 backdrop-blur-sm rounded-2xl shadow-xl p-4 md:p-6 mb-6 border-2 border-cyan-300">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-cyan-100 rounded-xl">
                <CheckSquare className="text-cyan-700" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-cyan-900 text-lg">إجراءات المحدد</h3>
                <p className="text-sm text-cyan-700">
                  المحدد: <span className="font-bold">{selectedOrderNumbers.size}</span> طلب
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setSelectAllVisible(true, filteredOrders)}
                disabled={
                  filteredOrders.length === 0 ||
                  isMarkingGirabatcom ||
                  isMarkingAsOld ||
                  isSendingFincart
                }
                className="bg-white border-2 border-cyan-400 text-cyan-800 font-bold px-5 py-3 rounded-xl hover:bg-cyan-50 disabled:opacity-50 transition-all flex items-center gap-2"
              >
                <CheckSquare size={18} />
                تحديد الكل
              </button>
              <button
                type="button"
                onClick={() => setSelectAllVisible(false, filteredOrders)}
                disabled={
                  selectedOrderNumbers.size === 0 ||
                  isMarkingGirabatcom ||
                  isMarkingAsOld ||
                  isSendingFincart
                }
                className="bg-white border-2 border-gray-300 text-gray-700 font-medium px-5 py-3 rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-all"
              >
                إلغاء التحديد
              </button>
              {/* <button
                type="button"
                onClick={handleCopySelectedPhones}
                disabled={
                  selectedOrderNumbers.size === 0 ||
                  isMarkingGirabatcom ||
                  isMarkingAsOld ||
                  isSendingFincart
                }
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold px-5 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Copy size={18} />
                نسخ أرقام الموبايل ({selectedOrderNumbers.size})
              </button> */}
              {canAct && (
                <>
                  <button
                    type="button"
                    onClick={handleSendSelectedToFincart}
                    disabled={
                      isOrdersLoading ||
                      selectedOrderNumbers.size === 0 ||
                      isMarkingGirabatcom ||
                      isMarkingAsOld ||
                      isSendingFincart
                    }
                    className="bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-400 text-white font-bold px-5 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isSendingFincart ? (
                      <>
                        <Loader className="animate-spin" size={18} />
                        جاري الإرسال...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        إرسال لـ Fincart ({selectedOrderNumbers.size})
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleMarkSelectedAsOld}
                    disabled={
                      isOrdersLoading ||
                      selectedOrderNumbers.size === 0 ||
                      isMarkingGirabatcom ||
                      isMarkingAsOld ||
                      isSendingFincart
                    }
                    className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-bold px-5 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isMarkingAsOld ? (
                      <>
                        <Loader className="animate-spin" size={18} />
                        جاري التحويل...
                      </>
                    ) : (
                      <>
                        <Archive size={18} />
                        تحويل للقديم ({selectedOrderNumbers.size})
                      </>
                    )}
                  </button>
                  {/* <button
                    type="button"
                    onClick={handleMarkSelectedAsGirabatcom}
                    disabled={
                      isOrdersLoading ||
                      selectedOrderNumbers.size === 0 ||
                      isMarkingGirabatcom ||
                      isMarkingAsOld ||
                      isSendingFincart
                    }
                    className="bg-slate-600 hover:bg-slate-700 disabled:bg-gray-400 text-white font-bold px-5 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isMarkingGirabatcom ? (
                      <>
                        <Loader className="animate-spin" size={18} />
                        جاري التحويل...
                      </>
                    ) : (
                      <>
                        <Building2 size={18} />
                        تحويل لـ Girabatcom ({selectedOrderNumbers.size})
                      </>
                    )}
                  </button> */}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 overflow-x-auto">
          {isOrdersLoading ? (
            <SkeletonLoader type="table" />
          ) : (
            <>
              {filteredOrders.length > 0 ? (
                <>
                  <table className="w-full min-w-[1200px] table-auto">
                    <thead>
                      <tr className="bg-gradient-to-r from-slate-50 to-cyan-50 border-b-2 border-slate-200">
                        <th className="p-2 text-center font-bold text-slate-800">
                          <div className="flex flex-col items-center gap-1">
                            <input
                              type="checkbox"
                              className="w-5 h-5 accent-cyan-600"
                              checked={
                                filteredOrders.length > 0 &&
                                filteredOrders.every((o) =>
                                  selectedOrderNumbers.has(o.orderNumber)
                                )
                              }
                              onChange={(e) =>
                                setSelectAllVisible(e.target.checked, filteredOrders)
                              }
                            />
                            <span className="text-xs">الطلب</span>
                          </div>
                        </th>
                        <th className="p-4 text-right font-bold text-slate-800">العميل</th>
                        <th className="p-4 text-right font-bold text-slate-800">العنوان</th>
                        <th className="p-4 text-center font-bold text-slate-800">الإجمالي</th>
                        <th className="p-4 text-right font-bold text-slate-800">المنتجات + الرابط</th>
                        <th className="p-4 text-right font-bold text-slate-800">ملاحظات</th>
                        <th className="p-4 text-center font-bold text-slate-800">نسخ</th>
                        <th className="p-4 text-center font-bold text-slate-800">إجراءات</th>
                        <th className="p-4 text-center font-bold text-slate-800">حذف</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.map((order) => (
                        <tr key={order.orderNumber} className="border-b border-gray-200 hover:bg-white/60 transition-colors">
                          {/* Order Number + Checkbox */}
                          <td className="p-2 text-center">
                            <div className="flex flex-col items-center gap-1">
                              <input
                                type="checkbox"
                                className="w-5 h-5 accent-cyan-600"
                                checked={selectedOrderNumbers.has(order.orderNumber)}
                                onChange={() => toggleSelectOrder(order.orderNumber)}
                              />
                              <span className="font-mono font-bold text-slate-700 text-sm">#{order.orderNumber}</span>
                              {order.sentToFincart ? (
                                <span
                                  title={
                                    order.fincartSentThisSession
                                      ? "تم الإرسال لـ Fincart في هذه الجلسة"
                                      : "مُرسل لـ Fincart مسبقاً"
                                  }
                                  className={`text-[10px] font-bold rounded px-1.5 py-0.5 border ${
                                    order.fincartSentThisSession
                                      ? "text-cyan-700 bg-cyan-50 border-cyan-200"
                                      : "text-orange-700 bg-orange-50 border-orange-200"
                                  }`}
                                >
                                  {order.fincartSentThisSession ? "Fincart ✓" : "old in fincart"}
                                </span>
                              ) : order.fincartDupSkipped ? (
                                <span
                                  title="تم تخطيه بسبب تكرار الرقم"
                                  className="text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5"
                                >
                                  DUP
                                </span>
                              ) : null}
                            </div>
                          </td>

                          {/* Customer Name */}
                          <td className="p-4">
                            {isAdmin(authState.role) && (
                                <button
                                  onClick={() => setEditingOrderId(order.orderNumber)}
                                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium transition-all mb-5"
                                >
                                  <div className="flex items-center justify-center gap-1">
                                    <Edit size={14} />
                                    تعديل
                                  </div>
                                </button>
                            )}
                            <div className="flex items-center gap-2">
                              <User size={16} className="text-gray-500" />
                              <span className="font-medium">{order.name}</span>
                            </div>

                          {/* Phone Numbers */}
                          <br />
                          <div className="flex items-center gap-2">
                          <Phone size={16} className="text-green-600" />

                          <div className="flex flex-col">
                            <span
                              onClick={() => navigator.clipboard.writeText(order.phoneNumber)}
                              className="text-green-600 hover:underline cursor-pointer"
                              title="Copy phone number"
                            >
                              {order.phoneNumber}
                            </span>

                            {order.phoneNumber2 !== "غير معروف" && (
                              <span
                                onClick={() => navigator.clipboard.writeText(order.phoneNumber2)}
                                className="text-green-600 hover:underline cursor-pointer"
                                title="Copy phone number"
                              >
                                {order.phoneNumber2}
                              </span>
                            )}
                          </div>
                        </div>
                          </td>

                          {/* Address */}
                          <td className="p-4 max-w-[200px]">
                            <div className="flex items-start gap-2">
                              <MapPin size={16} className="text-gray-500 mt-1 flex-shrink-0" />
                              <span style={{fontFamily : "cairo black"}} className="text-[13px] line-clamp-5">{order.address}</span>
                            </div>
                            <br />
                             <div className="flex items-center gap-2">
                              <Map size={16} className="text-gray-500" />
                              <span className="text-2xl text-slate-700">{order.state}</span>
                            </div>
                          </td>

                         

                          {/* Total */}
                          <td className="p-4 text-center">
                            <span className="font-bold text-xl text-green-600">{order.total} ج.م</span>
                          </td>

                          {/* Products + Drive Links - Merged */}
                          <td className="p-4">
                            {order.items.map((item, index) => (
                              <div key={`${item.id}-${index}`} className="mb-3">
                                <div className="flex items-start gap-2">
                                  <div className="flex-1">
                                    <div className="font-medium text-slate-700">{item.name}</div>
                                    <div style={{fontFamily:"cairo black"}} className="text-[13px] text-black-600">{item.type}</div>
                                    <div className="flex items-center gap-2 mt-1">
                                      {item.code.startsWith("CUSTOM") && (
                                        <span className="text-xs bg-green-100 px-2 py-1 rounded font-mono">
                                          {item.code}
                                        </span>
                                      )}
                                       {!item.code.startsWith("CUSTOM") && (
                                        <span className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                                          {item.code}
                                        </span>
                                      )}
                                      
                                      {item.driveLink && (
                                        <div className="flex items-center gap-1">
                                          <a
                                            href={item.driveLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-slate-600 hover:text-slate-800 transition-transform hover:scale-110"
                                            title="فتح الرابط"
                                          >
                                            <LinkIcon size={16} />
                                          </a>
                                          
                                          {item.code.startsWith("CUSTOM") && (
                                            <button
                                              onClick={() =>
                                                handleDownload(item.driveLink, `${order.orderNumber}-${item.id}.jpg`)
                                              }
                                              className="flex items-center gap-2 text-white hover:text-white transition-transform hover:scale-110 px-3 py-2"
                                              title="تحميل الصورة"
                                              style={{
                                                backgroundColor: "#15d225",
                                                borderRadius: "4px",
                                              }}
                                            >
                                              <Download size={16} />
                                              <span className="text-sm">Download</span>
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                {index < order.items.length - 1 && (
                                  <hr className="my-2 border-gray-200" />
                                )}
                              </div>
                            ))}
                          </td>

                          {/* Notes */}
                          <td className="p-4 max-w-[150px]">
                            <p style={{fontFamily:"cairo black"}} className="text-[16px]">{order.note || "—"}</p>
                          </td>

                          {/* Copy Buttons */}
                          <td className="p-4">
                            <div className="flex flex-col gap-2 min-w-[100px]">
                              <button
                                onClick={() => handleCopyOrderInfo(order)}
                                className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-6 py-4 rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-300 text-lg font-bold flex items-center justify-center gap-2"
                              >
                                <Copy size={14} />
                                العميل
                              </button>
                              <button
                                onClick={() => handleCopyItems(order)}
                                className="bg-gradient-to-r from-slate-500 to-cyan-500 text-white px-6 py-4 rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-300 text-lg font-bold flex items-center justify-center gap-2"
                              >
                                <Layers size={14} />
                                المنتجات
                              </button>
                              <button
                                onClick={() => handleCopyAllExcelDataNoReference(order)}
                                className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-4 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center justify-center gap-2"
                              >
                                <Copy size={16} />
                                نسخ
                              </button>
                            </div>
                          </td>

                          {/* Actions */}
                          <td className="p-2">
                            {canAct ? (
                            <div className="flex flex-col gap-2 min-w-[100px]">
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Confirmed")}
                                className="bg-green-600 text-white px-6 py-4 rounded-lg hover:bg-green-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <CheckCircle size={14} />
                                  ارجاع الطلب اي مؤكد
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Old")}
                                className="bg-gray-600 text-white px-6 py-4 rounded-lg hover:bg-gray-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Archive size={14} />
                                  قديم
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Girabatcom")}
                                className="bg-cyan-500 text-white px-6 py-4 rounded-lg hover:bg-cyan-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Building2 size={14} />
                                  Girabatcom
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Printing Hold")}
                                className="bg-slate-700 text-white px-6 py-4 rounded-lg hover:bg-slate-800 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Printer size={14} />
                                  Printing Hold
                                </div>
                              </button>
                            </div>
                            ) : (
                              <p className="text-center text-sm text-slate-500">عرض فقط</p>
                            )}
                          </td>

                          {/* Delete */}
                          <td className="p-4">
                            {canAct ? (
                            <div className="flex flex-col gap-2 min-w-[80px]">
                              <button
                                onClick={() => handleDeleteOrder(order.orderNumber)}
                                className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Trash2 size={14} />
                                  حذف
                                </div>
                              </button>
                            </div>
                            ) : (
                              <p className="text-center text-sm text-slate-500">عرض فقط</p>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Load More Button */}
                  {hasMore && (
                    <div className="text-center mt-8">
                      <button
                        onClick={loadMoreOrders}
                        disabled={isLoadingMore}
                        className="bg-gradient-to-r from-slate-700 to-cyan-600 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
                      >
                        {isLoadingMore ? (
                          <>
                            <Loader className="animate-spin" size={20} />
                            جاري التحميل...
                          </>
                        ) : (
                          <>
                            <ChevronLeft size={20} />
                            تحميل المزيد
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16">
                  <Printer className="mx-auto mb-4 text-gray-400" size={64} />
                  {loadedOrders.length > 0 ? (
                    <>
                      <p className="text-2xl text-gray-700 mb-2">لا توجد طلبات تطابق التصفية</p>
                      <p className="text-lg text-gray-500">جرّب تغيير نوع التصميم أو البحث</p>
                    </>
                  ) : (
                    <>
                      <p className="text-2xl text-gray-700 mb-2">لا توجد طلبات في Printed</p>
                      <p className="text-lg text-gray-500">سيتم عرض الطلبات المعلّقة للطباعة هنا</p>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrintedOrdersPage;